import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router";
import {Link} from "react-router-dom";
import Modal from '../../components/Modal';
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {
	callAlertWeb,
	callNativeWeb,
	clickSecurityKeypadEventWeb,
	footerShowEventWeb,
	hideProgressWeb,
	showProgressWeb
} from 'common/appInterface';

const LostAccidentCancelPage: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** Params */
	const {type = ""} = useParams();
	/** 다국어처리 */
	const {t, i18n} = useTranslation();
	
	const screenIdMap = useMemo(() => new Map([["useRegist", "STAT06"], ["lostCancel", "SERV05"], ["termination", "SERV07"]]), []);
	const screenNameMap = useMemo(() => new Map([["useRegist", t("STAT06.title")], ["lostCancel", t("SERV05.title")], ["termination", t("SERV07.title")]]), [t]);
	const accidentCodeMap = useMemo(() => new Map([["useRegist", "069"], ["lostCancel", "119"], ["termination", "811"]]), []);
	
	const screenId = stringUtils.nvlDefault(screenIdMap.get(type));
	const screenName = stringUtils.nvlDefault(screenNameMap.get(type));
	
	const [isModal, setModal] = useState(false);
	const cardInfo = location.state.info;
	const [cvc, setCvc] = useState("");
	const [password, setPassword] = useState("");
	const [cardVerify, setCardVerify] = useState<Partial<CardVerify>>({});
	
	const cvcRef = useRef<HTMLParagraphElement>(null);
	const passwordRef = useRef<HTMLParagraphElement>(null);
	
	/** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
	const callbackBackkeyEvent = useCallback(() => {
		navigate(-1);
	}, [navigate]);
	
	/** 보안키패드 입력요청 (clickSecurityKeypadEvent_Web (WEB > APP)) */
	const clickSecurityKeypadEvent = (type: string, length: number, title: string) => {
		clickSecurityKeypadEventWeb(screenId, screenName, type, length.toString(), title);
	};
	
	/** 보안키패드 입력 (typingSecurityKeypad_App (APP > WEB)) */
	const callbackTypingSecurityKeypadApp = useCallback((event) => {
		if (event.detail.keypadCode === "4") {
			setCvc(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		} else if (event.detail.keypadCode === "3") {
			setPassword(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		}
	}, []);
	
	/** 보안키패드 입력완료 처리 (completedPincode_App (APP > WEB)) */
	const callbackCompletedSecurityKeypadApp = useCallback((event) => {
		let length = event.detail.length === "0" ? (event.detail.keypadCode === "4" ? 3 : 2) : Number.parseInt(event.detail.length);
		
		if (event.detail.keypadCode === "4") {
			setCvc(Array<string>(length).fill("*").join(""));
			cvcRef.current?.classList.remove("error");
			setCardVerify({
				...cardVerify,
				cvcKeypadPid: event.detail.keypadPid,
				cvcEnteredValue: event.detail.enteredValue
			})
		} else if (event.detail.keypadCode === "3") {
			setPassword(Array<string>(length).fill("*").join(""));
			passwordRef.current?.classList.remove("error");
			setCardVerify({
				...cardVerify,
				passwordKeypadPid: event.detail.keypadPid,
				passwordEnteredValue: event.detail.enteredValue
			})
		}
	}, [cardVerify]);
	
	/** 카드인증 처리 */
	const clickDone = useCallback(() => {
		showProgressWeb();
		
		cvcRef.current?.classList.remove("error");
		passwordRef.current?.classList.remove("error");
		
		// 카드인증
		client.patch("/api/app/manage/card/verify/" + type, {
			...location.state.query,
			alternativeCardNo: cardInfo.alternativeCardNo, ...cardVerify,
			cardAccidentCode: accidentCodeMap.get(type)
		}).then((response) => {
			console.log(response);
			
			if (response.data.code === "00") {
				setModal(true);
			} else {
				if (response.data.code === "91") {
					cvcRef.current?.classList.add("error");
				} else if (response.data.code === "92") {
					passwordRef.current?.classList.add("error");
				} else {
					callAlertWeb("ERROR", response.data.code, response.data.message);
				}
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [cardVerify, accidentCodeMap, cardInfo, location, type]);
	
	// TODO TEST입력
	const testCall = (type: string) => {
		let keypadPid = "TEST_PID";
		let enteredValue = "TEST_ENTER_VALUE";
		
		if (type === "3") {
			keypadPid = "028B466229A2460DB42F11EC32B66C8E";
			enteredValue = "226e980249645915791383eb9a8f4e0283bb08a82f6d314c9fb9ee20c522bc6754068310e2fba291d517abc9478f2c67740db1c3";
		}
		
		let event = new CustomEvent("completKeypad", {
			detail: {
				keypadPid: keypadPid,
				keypadCode: type,
				enteredValue: enteredValue,
				length: "0"
			}
		});
		document.dispatchEvent(event);
	}
	
	/** 모달 닫기 */
	const closeModal = useCallback(() => {
		setModal(false);
		
		if (type === "termination") {
			navigate("/main", {state: {query: location.state.query, swiperIndex: 0, tabIndex: "1"}});
		} else {
			navigate("/main", {
				state: {
					query: location.state.query,
					swiperIndex: location.state.swiperIndex,
					tabIndex: location.state.tabIndex
				}
			});
		}
	}, [navigate, location, type]);
	
	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callNativeWeb(screenId, screenName);
		// 하단바 숨기기
		footerShowEventWeb("N");
		
		console.log("QUERY STATE");
		console.log(location.state);
		console.log(cardInfo);
		
		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("backpress", callbackBackkeyEvent);
		return () => document.removeEventListener("backpress", callbackBackkeyEvent);
	}, [callbackBackkeyEvent]);
	
	// 보안키패드 입력 이벤트 리스너 (typingSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
		return () => document.removeEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
	}, [callbackTypingSecurityKeypadApp]);
	
	// 보안키패드 결과전달 이벤트 리스너 (completedSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
		return () => document.removeEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
	}, [callbackCompletedSecurityKeypadApp]);
	
	return (
		<section className="pdb70">
			<div id="wrap">
				<div id="container" className="pcHidden">
					<section className="contents">
						<div className="singUpWrapSub mgt20 pdb0">
							<div className="inputBoxTy01">
								<label>
									<input placeholder=" " type="text" value={cardInfo.cardNo} readOnly/>
									<span>{t("VERIFY.header01")}</span>
								</label>
							</div>
							<div className="inputBoxTy01">
								<label onClick={() => clickSecurityKeypadEvent("4", 3, t("VERIFY.header02"))}>
									<input placeholder=" " type="password" value={cvc} maxLength={3} readOnly
										   className="inputReadOnly"
									/>
									<span>{t("VERIFY.header02")}</span>
									<p className="message" ref={cvcRef}
									   onClick={e => e.stopPropagation()}
									>
										{t("VERIFY.explan01")}
									</p>
								</label>
							</div>
							<div className="inputBoxTy01">
								<label className="mgb0"
									   onClick={() => clickSecurityKeypadEvent("3", 2, t("VERIFY.header03"))}
								>
									<input placeholder=" " type="password" value={password} maxLength={2} readOnly
										   className="inputReadOnly"
									/>
									<span>{t("VERIFY.header03")}</span>
									<p className="message" ref={passwordRef}
									   onClick={e => e.stopPropagation()}
									>
										{t("VERIFY.explan02")}
									</p>
									<p className="message error mgb0"
									   onClick={e => e.stopPropagation()}
									>
										{t("VERIFY.explan03")}
									</p>
								</label>
							</div>
						</div>
					</section>
					<section className="passWrap">
						<div className="singUpWrapSub pdt0">
							<div className="txtWrap">
								<p className="find_code">
									<Link to="/manage/password/change" state={{...location.state, info: cardInfo}}>
										{t("VERIFY.button01")}
									</Link>
								</p>
							</div>
							<div className="noticWrap">
								<dl>
									<dt>{t("VERIFY.explan04")}</dt>
								</dl>
							</div>
						</div>
						{process.env.REACT_APP_PROFILE !== "prod" &&
                            <>
                                <button className="btnPrimaryTy01" onClick={() => testCall("4")}>
                                    <span>CVC 입력</span>
                                </button>
                                <button className="btnPrimaryTy01" onClick={() => testCall("3")}>
                                    <span>카드비밀번호 입력</span>
                                </button>
                            </>
						}
					</section>
					<div className="btnArea">
						<div className="btnWrapTy01">
							<button type="button" className="btnPrimaryTy01"
									disabled={stringUtils.checkValueNull(cardVerify.cvcEnteredValue) || stringUtils.checkValueNull(cardVerify.passwordEnteredValue)}
									onClick={clickDone}
							>
								<span>{t("COMMON.button.done")}</span>
							</button>
						</div>
					</div>
				</div>
				{/* 사용등록 */}
				{type === "useRegist" &&
                    <Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2"
                           overlayClassName="dim" closeTimeoutMS={500}
                    >
                        <p className="message">{t("STAT06.popup02")}</p>
                        <dl>
							{/*<dt>{t("STAT06.popup03")}</dt>*/}
                            <dd><span>{cardInfo.cardNo}</span></dd>
                        </dl>
                        <div className="btnWrapTy02">
                            <button type="button" className="btnPrimaryTy01" onClick={closeModal}>
                                <span>{t("COMMON.button.confirm")}</span></button>
                        </div>
                    </Modal>
				}
				{/* 분실신고 해제 */}
				{type === "lostCancel" &&
                    <Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2"
                           overlayClassName="dim" closeTimeoutMS={500}
                    >
                        <p className="message">{t("SERV05.popup08")}</p>
                        <dl>
							{/*<dt>{t("SERV05.popup09")}</dt>*/}
                            <dd><span>{cardInfo.cardNo}</span></dd>
                        </dl>
                        <div className="btnWrapTy02">
                            <button type="button" className="btnPrimaryTy01" onClick={closeModal}>
                                <span>{t("COMMON.button.confirm")}</span></button>
                        </div>
                    </Modal>
				}
				{/* 카드해지 */}
				{type === "termination" &&
                    <Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2"
                           overlayClassName="dim" closeTimeoutMS={500}
                    >
                        <p className="message">{t("SERV07.popup08")}</p>
                        <dl>
							{/*<dt>{t("SERV07.popup09")}</dt>*/}
                            <dd><span>{cardInfo.cardNo}</span></dd>
                        </dl>
                        <div className="btnWrapTy02">
                            <button type="button" className="btnPrimaryTy01" onClick={closeModal}>
                                <span>{t("COMMON.button.confirm")}</span></button>
                        </div>
                    </Modal>
				}
			</div>
		</section>
	);
};

export default LostAccidentCancelPage;